/**
 * Turns the raw reason a campaign step was interrupted (`cadence_step.error_msg`, the
 * subject/body template errors, or `cadence.interrupt_message`) into something a rep can act on:
 * a short badge label plus one or two sentences saying what happened and what to do about it.
 *
 * The raw reasons are written by many code paths (the server runner, the Unipile error
 * classifier, the email sender, the extension) and some are years old, so this decodes by
 * pattern rather than by exact string and always falls back to the raw text. New server-side
 * messages should still be written for humans; this layer adds the badge and the fix.
 */

import { UserAtVariables } from '@poseidon/types/types/atVariables.type';

import { isEmailBounceInterruptMessage } from './cadence';
import { capitalize } from './collections';

export interface InterruptExplanation {
  /** Short uppercase label for the badge, e.g. 'MISSING INFO'. */
  badge: string;
  /** What happened and what to do about it, in rep terms. Ends with a period. */
  text: string;
  /** False when the reason was not recognised and `text` is the raw message plus a generic fix. */
  known: boolean;
}

/** Prefix of every "a variable could not be filled in" interrupt written by the server. */
export const MISSING_INFO_INTERRUPT_PREFIX = 'Missing info: ';

export function isMissingInfoInterruptMessage(message: string | null | undefined): boolean {
  return message != null && message.startsWith(MISSING_INFO_INTERRUPT_PREFIX);
}

/** Where a template variable lives, named the way the rep sees it in the step editor. */
export type TemplateLocation = 'subject' | 'body';
/** What the step is, so the sentence can say "email body" or "message". */
export type TemplateStepKind = 'email' | 'message' | 'InMail' | 'connection request' | 'step';

export type MissingInfoDetail =
  /** `{Company}` is in the template but the contact / team / sender has no value for it. */
  | { kind: 'no-value'; variable: string; location: TemplateLocation }
  /** `{Poop}` is not a variable Poseidon knows. */
  | { kind: 'unknown-variable'; variable: string; location: TemplateLocation }
  /** Free text like `{personalized}` was left in the template to be written per contact. */
  | { kind: 'placeholder'; placeholder: string; location: TemplateLocation }
  /** The contact's value for `{Company}` is itself a `{...}` placeholder, i.e. never filled. */
  | { kind: 'placeholder-value'; variable: string; location: TemplateLocation };

/** `FirstName` → `first name`, `MyLatestPost` → `my latest post`, `Company` → `company`. */
export function humanizeVariableName(variable: string): string {
  return variable
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/_/g, ' ')
    .toLowerCase();
}

function where(location: TemplateLocation, stepKind: TemplateStepKind): string {
  if (stepKind === 'email') return location === 'subject' ? 'the email subject' : 'the email body';
  if (location === 'subject') return 'the subject';
  return stepKind === 'step' ? 'this step' : `the ${stepKind}`;
}

/**
 * The interrupt message the server records when a step's template could not be filled in for a
 * contact. Names every variable and says where it is and what fixes it, e.g.
 * "Missing info: {Company} has no value for this contact, so the email body can't be filled in.
 *  Add the company to the contact (or edit the step to drop {Company}), then resume the campaign."
 */
/** `{MyCalendarLink}`, `{MyName}`, … are filled from the sender's own profile, not the contact. */
export function isSenderVariable(variable: string): boolean {
  return (UserAtVariables as readonly string[]).includes(variable);
}

/** `MyCalendarLink` → `calendar link`, `MyName` → `name` (the "my" is implied by "your"). */
function senderFieldName(variable: string): string {
  return humanizeVariableName(variable).replace(/^my /, '');
}

/**
 * @param action what the rep does once it's fixed — "then resume the campaign" for a campaign
 *   step, something else for a one-off manual send that has no campaign to resume.
 */
export function missingInfoInterruptMessage(
  details: MissingInfoDetail[],
  stepKind: TemplateStepKind = 'step',
  action = 'then resume the campaign',
): string {
  const sentences = details.map((d) => {
    const loc = where(d.location, stepKind);
    switch (d.kind) {
      case 'no-value':
        if (d.variable === 'MyLatestPost')
          return `{${d.variable}} needs a recent LinkedIn post of yours, and Poseidon hasn't found one, so ${loc} can't be filled in. Post on LinkedIn (or edit the step to drop {${d.variable}}), ${action}.`;
        if (isSenderVariable(d.variable))
          return `{${d.variable}} has no value because your ${senderFieldName(d.variable)} isn't set, so ${loc} can't be filled in. Add your ${senderFieldName(d.variable)} in Settings → Profile (or edit the step to drop {${d.variable}}), ${action}.`;
        return `{${d.variable}} has no value for this contact, so ${loc} can't be filled in. Add the ${humanizeVariableName(d.variable)} to the contact (or edit the step to drop {${d.variable}}), ${action}.`;
      case 'placeholder-value':
        if (isSenderVariable(d.variable))
          return `Your ${senderFieldName(d.variable)} in Settings → Profile is still a {…} placeholder, so {${d.variable}} in ${loc} can't be filled in. Replace it with the real value, ${action}.`;
        return `The contact's ${humanizeVariableName(d.variable)} is still a {…} placeholder, so {${d.variable}} in ${loc} can't be filled in. Replace it with the real value on the contact, ${action}.`;
      case 'unknown-variable':
        return `{${d.variable}} in ${loc} isn't a variable Poseidon knows, so it can't be filled in. Edit the step to fix or remove it, ${action}.`;
      case 'placeholder':
        return `${capitalize(loc)} still contains the placeholder {${d.placeholder}} that has to be written for this contact. Edit this contact's step and replace it, ${action}.`;
    }
  });
  const unique = Array.from(new Set(sentences));
  return `${MISSING_INFO_INTERRUPT_PREFIX}${unique.join(' ')}`;
}

type Rule = { badge: string; test: (reason: string) => boolean; text: (reason: string) => string };

const RECONNECT_LINKEDIN = 'Reconnect LinkedIn in Settings → Integrations, then resume the campaign.';
const RECONNECT_EMAIL = 'Reconnect your mailbox in Settings → Integrations, then resume the campaign.';
const RESUME_TO_RETRY = 'Resume the campaign to try again.';

const exact =
  (...values: string[]) =>
  (reason: string) =>
    values.includes(reason);
const matches =
  (...patterns: RegExp[]) =>
  (reason: string) =>
    patterns.some((p) => p.test(reason));

/** Order matters: the first matching rule wins, so specific patterns come before broad ones. */
const RULES: Rule[] = [
  // --- template / content -------------------------------------------------------------------
  {
    badge: 'MISSING INFO',
    // Anywhere, not only at the start: a caller may have wrapped the reason ("Failed to …: Missing info: …").
    test: (r) => r.includes(MISSING_INFO_INTERRUPT_PREFIX),
    text: (r) => r.slice(r.indexOf(MISSING_INFO_INTERRUPT_PREFIX) + MISSING_INFO_INTERRUPT_PREFIX.length),
  },
  {
    badge: 'MISSING INFO',
    test: matches(
      /^unresolved variable$/i,
      /variable with no value/i,
      /^value for \S+ is undefined$/i,
      /^unknown variable /i,
    ),
    text: () =>
      'A personalization variable in this step has no value for this contact (a missing contact field, or a leftover {placeholder}). Add the missing info to the contact or edit the step, then resume the campaign.',
  },
  {
    badge: 'EMPTY STEP',
    test: matches(/^Missing subject (or|and) body for email$/i, /^Step does not have (message|InMail) content$/i),
    text: (r) =>
      /email/i.test(r)
        ? 'This email step has no subject or body for this contact, so nothing can be sent. Open the step, add the subject and body, then resume the campaign.'
        : 'This step has no message text, so nothing can be sent. Open the step, add the message, then resume the campaign.',
  },

  // --- retries exhausted: must come before the LinkedIn rules, since the detail quotes them ------
  {
    badge: 'GAVE UP',
    test: matches(/^Gave up after \d+ attempts/i),
    text: (r) => {
      const detail = r.replace(/^Gave up after \d+ attempts\s*[—-]\s*/i, '');
      return `Poseidon retried this step automatically and it kept failing (${detail.replace(/\.$/, '')}). ${RESUME_TO_RETRY} If it fails again, check the detail above.`;
    },
  },

  // --- email ----------------------------------------------------------------------------------
  {
    badge: 'EMAIL MISSING',
    test: exact('Contact does not have any professional emails set'),
    text: () =>
      "This contact has no email address, so the email step can't send. Add an email address to the contact (or enrich the contact), then resume the campaign.",
  },
  {
    badge: 'EMAIL BOUNCED',
    test: (r) => isEmailBounceInterruptMessage(r),
    text: (r) =>
      `${r.replace(/\.$/, '')}. Fix or replace the contact's email address, then resume the campaign — the bounced email won't be sent again.`,
  },
  {
    badge: 'UNSUBSCRIBED',
    test: matches(/suppression list/i),
    text: () =>
      "This address unsubscribed or hard-bounced before, so Poseidon won't email it again. Reach the contact another way (call or LinkedIn), or remove the email steps for this contact.",
  },
  {
    badge: 'EMAIL NOT CONNECTED',
    test: matches(/^Account has no email integrations/i),
    text: () =>
      `Your mailbox isn't connected to Poseidon, so no email can go out. Connect your email in Settings → Integrations, then resume the campaign.`,
  },
  {
    badge: 'EMAIL NOT CONNECTED',
    test: matches(
      /^Email account needs to be reconnected/i,
      /Could not check for received emails/i,
      /^Failed to (sync|fetch) emails/i,
    ),
    text: () => `Poseidon lost access to your mailbox, so it couldn't check for replies or send. ${RECONNECT_EMAIL}`,
  },
  {
    badge: 'NO SENDING INBOX',
    test: matches(/^No dedicated sending inbox is available/i),
    text: () =>
      "This campaign sends from a dedicated inbox, but none is assigned or ready. Assign a sending inbox in the campaign's email settings (or switch it to your own mailbox), then resume the campaign.",
  },
  {
    badge: 'VERIFICATION EMAIL MISSING',
    test: exact('Failed to type email for verification'),
    text: () =>
      'LinkedIn asked for an email verification that Poseidon could not complete. Open LinkedIn, finish the verification, then resume the campaign.',
  },
  {
    badge: 'TIMEZONE',
    test: matches(/ is not a valid timezone$/i),
    text: (r) =>
      `${r}, so Poseidon can't work out your sending hours. Set a valid timezone in Settings → Profile, then resume the campaign.`,
  },

  // --- LinkedIn relationship / content ---------------------------------------------------------
  {
    badge: 'NOT CONNECTED',
    test: matches(
      /^Can't message a non-connected person$/i,
      /^Cannot send message - not connected to contact$/i,
      /not a 1st-degree connection/i,
    ),
    text: () =>
      "You're not connected with this contact on LinkedIn, so a direct message can't be sent. Send a connection request first (or change the step to InMail or email), then resume the campaign.",
  },
  {
    badge: 'ALREADY CONNECTED',
    test: matches(/^Cannot send InMail - already connected/i, /already connected to this contact/i),
    text: () =>
      "You're already connected with this contact, so this step isn't needed. Change it to a regular message (or skip it), then resume the campaign.",
  },
  {
    badge: 'CONVERSATION DISABLED',
    test: exact('Contact has disabled conversations'),
    text: () =>
      "This contact doesn't accept LinkedIn messages from people they're not connected with. Send a connection request first, or reach them by email, then resume the campaign.",
  },
  {
    badge: 'SALES NAV NEEDED',
    test: matches(/Sales Navigator subscription/i),
    text: () =>
      'This step needs a LinkedIn Sales Navigator subscription on your account. Use a Sales Navigator account, or change the step to a regular message or email, then resume the campaign.',
  },
  {
    badge: 'INVITE PENDING',
    test: matches(/connection request is already pending/i),
    text: () =>
      "A connection request to this contact is already pending (or an earlier one was declined), so LinkedIn won't take another. Wait for them to accept, or remove the connect step for this contact, then resume the campaign.",
  },
  {
    badge: 'LINKEDIN PROFILE',
    test: matches(
      /no provider_id/i,
      /Could not decode LinkedIn member ID/i,
      /no linkedin foreign mapping/i,
      /^Failed to fetch LinkedIn profile/i,
    ),
    text: () =>
      "Poseidon couldn't identify this contact's LinkedIn profile. Open the contact, check its LinkedIn URL points at the right person, then resume the campaign.",
  },
  {
    badge: 'NO POSTS',
    test: matches(/^Could not get post ID from recent posts/i),
    text: () =>
      'This contact has no recent LinkedIn posts to like. Skip this step for them (or wait for a new post), then resume the campaign.',
  },

  // --- LinkedIn account / limits ---------------------------------------------------------------
  {
    badge: 'LINKEDIN DISCONNECTED',
    test: matches(
      /LinkedIn account (has been disconnected|connection is no longer valid|connection was lost|credentials have expired)/i,
      /LinkedIn requires verification/i,
    ),
    text: () => `Poseidon lost access to your LinkedIn account, so no LinkedIn step can run. ${RECONNECT_LINKEDIN}`,
  },
  {
    badge: 'LINKEDIN LIMIT',
    test: matches(
      /connection limit (reached|never cleared)/i,
      /out of LinkedIn InMail credits/i,
      /limiting Sales Navigator lookups/i,
    ),
    text: (r) =>
      /credits/i.test(r)
        ? 'Your account is out of LinkedIn InMail credits. Wait for the weekly refill (or switch the step to a message or email), then resume the campaign.'
        : 'LinkedIn capped how many requests this account can send for now. Wait a day for the limit to clear, then resume the campaign.',
  },
  {
    badge: 'LINKEDIN REJECTED',
    test: matches(/^LinkedIn rejected this request/i),
    text: () =>
      "LinkedIn refused this action, usually because of rate limits or the contact's privacy settings. Check the contact's profile allows it, wait a day, then resume the campaign.",
  },
  {
    badge: 'TEMPORARY',
    test: matches(
      /temporarily unavailable/i,
      /Will retry automatically/i,
      /^Temporary (issue|server issue)/i,
      /^Rate limited/i,
    ),
    text: () =>
      `LinkedIn (or the service Poseidon uses to reach it) was unavailable when this step ran. ${RESUME_TO_RETRY}`,
  },
  {
    badge: 'LINKEDIN ERROR',
    test: matches(/^Request (error|failed)/i, /^Failed(:| to )/i, /^Voyager request failed/i, /^Invalid response/i),
    text: (r) => `${r.replace(/\.$/, '')}. ${RESUME_TO_RETRY} If it keeps failing, contact support with this message.`,
  },
  {
    badge: 'INTERNAL ERROR',
    test: exact('internal error', 'Step Error'),
    text: () =>
      `Something went wrong inside Poseidon while running this step. ${RESUME_TO_RETRY} If it happens again, contact support.`,
  },
];

export function explainInterruptReason(reason: string | null | undefined): InterruptExplanation {
  const raw = reason?.trim() ?? '';
  if (raw === '') {
    return { badge: 'INTERRUPTED', text: 'This step was interrupted. Resume the campaign to try again.', known: false };
  }
  for (const rule of RULES) {
    if (rule.test(raw)) return { badge: rule.badge, text: rule.text(raw), known: true };
  }
  return {
    badge: 'INTERRUPTED',
    text: `${raw.replace(/\.$/, '')}. Fix the cause, then resume the campaign.`,
    known: false,
  };
}

/** Human step type for tooltips: `likeRecentPosts` → "Like recent posts". */
export function humanizeStepType(stepType: string): string {
  const spaced = stepType
    .replace(/_/g, ' ')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .toLowerCase();
  return capitalize(spaced);
}

/** One line for the step strip: "Email step interrupted — <what happened + fix>". */
export function interruptedStepTooltip(stepType: string, reason: string | null | undefined): string {
  return `${humanizeStepType(stepType)} step interrupted — ${explainInterruptReason(reason).text}`;
}
